
import React, { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Clock, Share2, Bookmark, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface ModernPolicyLayoutProps {
  title: string;
  estimatedReadTime?: string;
  lastUpdated?: string;
  children: React.ReactNode;
  tableOfContents?: Array<{
    id: string;
    title: string;
    level: number;
  }>;
}

const ModernPolicyLayout: React.FC<ModernPolicyLayoutProps> = ({
  title,
  estimatedReadTime = "5 min read",
  lastUpdated = "Last updated: May 10, 2025",
  children,
  tableOfContents = []
}) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('');
  const [isBookmarked, setIsBookmarked] = useState(false);

  const handleBack = () => {
    navigate('/legal-hub');
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Header */}
        <div className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-xl">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Legal Hub
              </Button>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsBookmarked(!isBookmarked)}
                  className={cn(
                    "text-slate-400 hover:text-slate-200",
                    isBookmarked && "text-yellow-400"
                  )}
                >
                  <Bookmark className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-slate-200"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-slate-200"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="max-w-4xl">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent mb-4 font-orbitron">
                {title}
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {estimatedReadTime}
                </div>
                <Separator orientation="vertical" className="h-4" />
                <span>{lastUpdated}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Table of Contents - Desktop */}
            {tableOfContents.length > 0 && (
              <div className="hidden lg:block">
                <Card className="sticky top-8 bg-slate-800/30 border-slate-700/50 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-200 mb-4 font-space-mono">
                      Table of Contents
                    </h3>
                    <ScrollArea className="h-[60vh]">
                      <nav className="space-y-2">
                        {tableOfContents.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => scrollToSection(item.id)}
                            className={cn(
                              "w-full text-left text-sm py-2 px-3 rounded-md transition-colors",
                              "hover:bg-slate-700/50 hover:text-slate-200",
                              item.level === 2 && "ml-4 text-slate-400",
                              item.level === 3 && "ml-8 text-slate-500",
                              activeSection === item.id && "bg-cyan-900/30 text-cyan-400 border-l-2 border-cyan-400"
                            )}
                          >
                            {item.title}
                          </button>
                        ))}
                      </nav>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Main Content */}
            <div className={cn("space-y-8", tableOfContents.length > 0 ? "lg:col-span-3" : "lg:col-span-4")}>
              <Card className="bg-slate-800/30 border-slate-700/50 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="prose prose-slate prose-invert max-w-none">
                    <style>{`
                      .prose h1, .prose h2, .prose h3 {
                        font-family: 'Orbitron', sans-serif;
                        background: linear-gradient(135deg, #22d3ee, #a855f7);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                      }
                      
                      .prose h2 {
                        border-top: 1px solid rgba(51, 65, 85, 0.5);
                        padding-top: 2rem;
                        margin-top: 3rem;
                      }
                      
                      .prose p {
                        color: #e2e8f0;
                        line-height: 1.8;
                      }
                      
                      .prose ul li {
                        color: #cbd5e1;
                      }
                      
                      .prose strong {
                        color: #22d3ee;
                        font-weight: 600;
                      }
                      
                      .prose a {
                        color: #22d3ee;
                        text-decoration: none;
                        border-bottom: 1px solid transparent;
                        transition: border-color 0.2s;
                      }
                      
                      .prose a:hover {
                        border-bottom-color: #22d3ee;
                      }
                    `}</style>
                    {children}
                  </div>
                </CardContent>
              </Card>

              {/* Footer Actions */}
              <Card className="bg-gradient-to-r from-slate-800/30 to-slate-700/30 border-slate-600/50">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-200 mb-1">Questions about this policy?</h3>
                      <p className="text-sm text-slate-400">Contact our legal team for clarification</p>
                    </div>
                    <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white">
                      Contact Legal Team
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ModernPolicyLayout;
